import { useHistory, useLocation, useParams } from 'react-router';
import { useDatasetSessionStorageContext } from '../../../hooks/context/useDatasetSessionStorageContext';
import { useDebouncedEffect } from '../../../hooks/useDebouncedEffect';
import useRoutes from '../../../hooks/useRoutes';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { isFromPageReload } from '../../../utils/isFromPageReload';
import { getDatasetUrlParams } from '../../../utils/getDatasetUrlParams';
import { Dispatch, SetStateAction, useRef } from 'react';
import { GlobalLocationStateType } from '../../../types/types';
import { debugLogger } from '../../../debug/DebugLogger';

const log = debugLogger.scope('filters');

export const useDatasetFilterFirstRedirectGuard = (
  setDatasetFirstProcessed: Dispatch<SetStateAction<boolean>>
) => {
  const routes = useRoutes();
  const history = useHistory();
  const { datasetId } = useParams<{ datasetId: string }>();
  const { pathname, state } = useLocation<GlobalLocationStateType>();

  const {
    dataset,
    peersetFilterFirstProcessed,
    setActiveFilters,
    setPeersetFilterFirstProcessed,
  } = useDatasetContext();
  const {
    fromItemExternalUrl,
    getDatasetSessionStorageState,
    setDatasetSessionStorageState,
  } = useDatasetSessionStorageContext();

  const skipNextFilterRedirectRef = useRef<boolean>(false);

  useDebouncedEffect(
    () => {
      const sessionState = getDatasetSessionStorageState();

      // Do not open filterFirst if it is from item external url
      if (fromItemExternalUrl) return;

      setDatasetFirstProcessed(false);
      const isFilterFirstDataset = dataset?.filterFirst;

      if (!isFilterFirstDataset || !datasetId) {
        setDatasetFirstProcessed(true);
        return;
      }

      // 1) consume state returned from filter (handle filter-back state)
      if (state?.omitFilterFirstFilter) {
        /** @debugFeature filters Logs the filterFirst redirect guard consuming the return-from-filter state — no redirect back to the filter. */
        log('filterFirst redirect skipped, returned from filter', () => ({
          datasetId,
        }));
        skipNextFilterRedirectRef.current = true;
        setDatasetFirstProcessed(true);
        // cleanup location.state
        history.replace(pathname, {
          ...(history.location.state ?? {}),
          omitFilterFirstFilter: undefined,
        });
        return;
      }

      // 2) after cleanup, don't perform redirect back to filter
      if (skipNextFilterRedirectRef.current) {
        setDatasetFirstProcessed(true);
        skipNextFilterRedirectRef.current = false;
        return;
      }

      // 3) standard filterFirst redirect
      setDatasetFirstProcessed(true);
      // Do not open filterFirst if it was already closed or submitted on a previous peerset with the same filter
      if (peersetFilterFirstProcessed) {
        /** @debugFeature filters Logs the filterFirst redirect guard skipping — filterFirst was already processed (submit, hydrate or peerset carryover). */
        log('filterFirst redirect skipped, already processed', () => ({
          datasetId,
        }));
        return;
      }

      // 4) Same-dataset URL-bar nav (e.g. /item/X -> dataset root): skip
      // filter and hydrate persisted filters. Same-URL Enter / F5 reports
      // 'reload' so isFromPageReload() short-circuits and the filter opens.
      // Tracker may have rotated already — fall back to previousPathname
      // when lastPathname equals current.
      const previousPathname =
        sessionState.lastPathname && sessionState.lastPathname !== pathname
          ? sessionState.lastPathname
          : sessionState.previousPathname;
      const previousDatasetId = getDatasetUrlParams(
        previousPathname ?? ''
      ).datasetId;
      if (
        !isFromPageReload() &&
        previousDatasetId &&
        previousDatasetId === datasetId &&
        previousPathname !== pathname
      ) {
        const persistedFilters =
          sessionState.persistedFilterFirstFilters?.[datasetId];

        /** @debugFeature filters Logs the same-dataset navigation branch of the filterFirst guard: persisted filters hydrated, or — when none exist — a silent fall-through with no filter modal and no filters. */
        log('filterFirst same-dataset navigation', () => ({
          datasetId,
          previousPathname,
          pathname,
          hydratedPersistedFilters: Boolean(persistedFilters),
          persistedFilters,
        }));

        if (persistedFilters) {
          setActiveFilters(persistedFilters);
          setPeersetFilterFirstProcessed(true);
        }
        return;
      }

      // Update return pathname, if entering from item url after reload
      let fromPathname: string = pathname;
      const fromPageReload = isFromPageReload();

      if (fromPageReload && sessionState?.reloadFromItem) {
        setDatasetSessionStorageState({ reloadFromItem: undefined });
        fromPathname = routes.datasetList(datasetId);
      }

      /** @debugFeature filters Logs the standard filterFirst redirect to the filter form, with the reload detection result. */
      log('filterFirst redirecting to filter form', () => ({
        datasetId,
        fromPageReload,
        fromPathname,
      }));

      // Pass current pathname so closeFilter can return to the original URL
      // (incl. /item/:itemId) — locate-by-id flow then runs on it.
      history.replace(routes.datasetDetailMasterFilter(datasetId), {
        state,
        fromFilterFirstInit: true,
        fromPathname,
      });
    },
    [fromItemExternalUrl],
    0
  );
};
